import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import ProjectList from './components/ProjectList';
import InternalProjectDetail from './components/InternalProjectDetail';
import Portfolios from './components/Portfolios';
import PortfolioDetail from './components/PortfolioDetail';
import Requests from './components/Requests';
import Raid from './components/Raid';
import TemplatesPage from './components/TemplatesPage';
import TimesheetsPage from './components/TimesheetsPage';
import Settings from './components/Settings';
import CapacityPlanningPage from './components/CapacityPlanningPage';
import OkrsPage from './components/OkrsPage';
import RoadmapsPage from './components/RoadmapsPage';
import TeamManagement from './components/TeamManagement';
import FinancialsPage from './components/FinancialsPage';
import StrategicAlignmentPage from './components/StrategicAlignmentPage';
import OrgAdminPage from './components/OrgAdminPage';
import Beacon from './components/Beacon';
import { hasPageAccess } from './utils/rbac';
import { DiscussionProvider } from './contexts/DiscussionContext';
import { InlineDiscussion } from './components/discussions/InlineDiscussion';
import { setApiTokens, usersApi, DbUser, authApi } from './api/client';
import { supabase } from './lib/supabase';

import { UserSettings } from './components/UserSettings';
import { TenantProvider, useTenant } from './contexts/TenantContext';

type ViewType =
  | 'overview'
  | 'dashboard'
  | 'strategic-alignment'
  | 'portfolios'
  | 'requests'
  | 'projects'
  | 'financials'
  | 'capacity'
  | 'okrs'
  | 'roadmaps'
  | 'raid'
  | 'templates'
  | 'timesheets'
  | 'beacon'
  | 'settings'
  | 'team'
  | 'management';

const ALL_NAV_ITEMS: Array<{ id: ViewType; label: string; path: string }> = [
  { id: 'overview', label: 'Overview', path: '/overview' },
  { id: 'dashboard', label: 'Workspace', path: '/dashboard' },
  { id: 'strategic-alignment', label: 'Strategic Alignment', path: '/strategic-alignment' },
  { id: 'beacon', label: 'Beacon', path: '/beacon' },
  { id: 'portfolios', label: 'Portfolios', path: '/portfolios' },
  { id: 'requests', label: 'Requests', path: '/requests' },
  { id: 'projects', label: 'Projects', path: '/projects' },
  { id: 'financials', label: 'Financials', path: '/financials' },
  { id: 'capacity', label: 'Capacity', path: '/capacity' },
  { id: 'okrs', label: 'OKRs', path: '/okrs' },
  { id: 'roadmaps', label: 'Roadmaps', path: '/roadmaps' },
  { id: 'raid', label: 'RAID', path: '/raid' },
  { id: 'templates', label: 'Templates', path: '/templates' },
  { id: 'timesheets', label: 'Timesheets', path: '/timesheets' },
  { id: 'team', label: 'Team', path: '/team' },
];

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { currentTenant, refreshTenant, isLoading: tenantLoading } = useTenant();

  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dbUser, setDbUser] = useState<DbUser | null>(null);
  const [dbUserLoading, setDbUserLoading] = useState(true);

  // Profile dropdown
  const [profileOpen, setProfileOpen] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Add Organization modal
  const [addOrgOpen, setAddOrgOpen] = useState(false);
  const [addOrgMode, setAddOrgMode] = useState<'join' | 'create' | null>(null);
  const [addOrgCode, setAddOrgCode] = useState('');
  const [addOrgName, setAddOrgName] = useState('');
  const [addOrgLoading, setAddOrgLoading] = useState(false);
  const [addOrgError, setAddOrgError] = useState('');
  const [addOrgLookup, setAddOrgLookup] = useState<string | null>(null);

  // Org switcher
  const [myTenants, setMyTenants] = useState<Array<{ id: string; name: string; slug: string; userId: string }>>([]);
  const [switchingTenant, setSwitchingTenant] = useState<string | null>(null);

  // Load user's tenants when authenticated
  useEffect(() => {
    if (!token) { setMyTenants([]); return; }
    authApi.myTenants()
      .then(res => {
        console.log('[org-switcher] my-tenants response:', res.data);
        setMyTenants(res.data.tenants || []);
      })
      .catch(err => {
        console.error('[org-switcher] my-tenants failed:', err?.response?.status, err?.message);
      });
  }, [token]);

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      setSwitchingTenant(tenantId);
      const res = await authApi.switchTenant(tenantId);
      if (res.data.accessToken) {
        setApiTokens({ accessToken: res.data.accessToken });
        localStorage.setItem('token', res.data.accessToken);
        setToken(res.data.accessToken);
        setProfileOpen(false);
        await refreshTenant();
        // Navigate to the new tenant's dashboard
        const newTenant = myTenants.find(t => t.id === tenantId);
        if (newTenant) {
          navigate(`/${newTenant.slug}/dashboard`);
        }
      }
    } catch (e: any) {
      console.error('Failed to switch org:', e);
    } finally {
      setSwitchingTenant(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add Org: tenant code lookup
  useEffect(() => {
    const code = addOrgCode.trim();
    if (code.length < 3) { setAddOrgLookup(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res = await authApi.lookupTenant(code);
        setAddOrgLookup(res.data.success && res.data.name ? res.data.name : null);
      } catch { setAddOrgLookup(null); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [addOrgCode]);

  const handleAddOrgSubmit = async () => {
    setAddOrgError('');
    setAddOrgLoading(true);
    try {
      if (addOrgMode === 'join') {
        // Register a new user record in the target tenant
        const res = await authApi.register({
          email: dbUser?.email || '',
          password: Math.random().toString(36) + 'Aa1!',
          tenantCode: addOrgCode.trim(),
        });
        // Switch to new tenant
        setApiTokens({ accessToken: res.data.accessToken });
        localStorage.setItem('token', res.data.accessToken);
        setToken(res.data.accessToken);
        refreshTenant();
        refreshDbUser();
      } else if (addOrgMode === 'create') {
        const res = await authApi.register({
          email: dbUser?.email || '',
          password: Math.random().toString(36) + 'Aa1!',
          tenantName: addOrgName.trim(),
        });
        setApiTokens({ accessToken: res.data.accessToken });
        localStorage.setItem('token', res.data.accessToken);
        setToken(res.data.accessToken);
        refreshTenant();
        refreshDbUser();
      }
      setAddOrgOpen(false);
      setAddOrgMode(null);
      setAddOrgCode('');
      setAddOrgName('');
    } catch (err: any) {
      setAddOrgError(err?.response?.data?.message || 'Failed');
    } finally {
      setAddOrgLoading(false);
    }
  };

  // Initialize from stored token or Supabase OAuth redirect
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Fast path: validate the stored app token BEFORE showing the app, so a stale token
      // never renders a broken "User / 403" shell. The axios client transparently re-mints
      // the token from the Supabase session on 401/403 (or redirects to a clean login), so
      // here we just probe /users/me and adopt whatever token survives.
      const stored = localStorage.getItem('token');
      if (stored) {
        setApiTokens({ accessToken: stored });
        try {
          await usersApi.me();
          if (!cancelled) setToken(localStorage.getItem('token'));
        } catch {
          if (!cancelled) {
            localStorage.removeItem('token');
            setApiTokens({ accessToken: null });
            setToken(null);
          }
        }
        if (!cancelled) setIsLoading(false);
        return;
      }

      // Check if this is a Supabase OAuth redirect (PKCE uses ?code= with long random value)
      const params = new URLSearchParams(window.location.search);
      const codeParam = params.get('code');
      const hasAuthParams = window.location.hash.includes('access_token') ||
        (codeParam && codeParam.length > 20); // Supabase codes are long; invite codes are short

      if (!hasAuthParams) {
        // No stored token and no OAuth redirect — just show login
        setIsLoading(false);
        return;
      }

      // Handle Supabase OAuth callback
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token && !cancelled) {
          const res = await authApi.supabaseExchange(session.access_token);
          window.history.replaceState({}, '', '/');
          if (res.data.accessToken) {
            // Flow 2: single tenant — auto login
            setApiTokens({ accessToken: res.data.accessToken });
            localStorage.setItem('token', res.data.accessToken);
            setToken(res.data.accessToken);
            refreshTenant();
          } else if (res.data.needsTenantPicker || res.data.needsTenant) {
            // Check if there's a saved invite code from before the OAuth redirect
            const savedInviteCode = localStorage.getItem('oauth_invite_code');
            localStorage.removeItem('oauth_invite_code');

            if (savedInviteCode && res.data.needsTenant && res.data.googleEmail) {
              // Auto-register with the invite code instead of showing create/join
              try {
                const regRes = await authApi.register({
                  email: res.data.googleEmail,
                  password: Math.random().toString(36) + 'Aa1!',
                  tenantCode: savedInviteCode,
                });
                if (regRes.data.accessToken) {
                  setApiTokens({ accessToken: regRes.data.accessToken });
                  localStorage.setItem('token', regRes.data.accessToken);
                  setToken(regRes.data.accessToken);
                  refreshTenant();
                  if (!cancelled) setIsLoading(false);
                  return;
                }
              } catch {
                // Fall through to normal create/join flow if auto-join fails
              }
            }

            // Flow 1 (no tenant) or Flow 3 (multiple tenants) — pass to Login
            localStorage.setItem('supabase_pending_email', res.data.googleEmail || '');
            localStorage.setItem('supabase_pending_id', res.data.googleId || '');
            localStorage.setItem('supabase_pending_tenants', JSON.stringify(res.data.tenants || []));
            if (res.data.needsTenantPicker) {
              localStorage.setItem('supabase_pending_picker', 'true');
            }
          }
        }
      } catch {
        // Supabase session check failed
      }

      if (!cancelled) setIsLoading(false);
    }

    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch DB user when token is set
  useEffect(() => {
    if (!token) {
      setDbUser(null);
      setDbUserLoading(false);
      return;
    }
    setDbUserLoading(true);
    usersApi.me().then((res) => setDbUser(res.data)).catch(() => {
      return usersApi.ensure().then((res) => setDbUser(res.data)).catch(() => {});
    }).finally(() => setDbUserLoading(false));
  }, [token]);

  const refreshDbUser = useCallback(() => {
    usersApi.me().then((res) => setDbUser(res.data)).catch(() => {});
  }, []);

  const handleLogin = useCallback((newToken: string, _user: any) => {
    setToken(newToken);
    setApiTokens({ accessToken: newToken });
    // dbUser will be fetched by the effect above
    // Refresh tenant context since it may have loaded before token was set
    refreshTenant();
  }, [refreshTenant]);

  const isAuthenticated = !!token;
  const user = dbUser ? {
    sub: dbUser.id,
    email: dbUser.email || '',
    username: dbUser.displayName || dbUser.username || dbUser.email || '',
  } : null;

  // Tenant slug for URL prefix
  const tenantSlug = currentTenant?.slug || '';
  const base = tenantSlug ? `/${tenantSlug}` : '';

  // Reset scroll on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  // Redirect to /:tenantSlug/dashboard when on root or bare slug
  useEffect(() => {
    if (!isAuthenticated || isLoading || !tenantSlug) return;
    const path = location.pathname;
    if (path === '/' || path === `/${tenantSlug}`) {
      navigate(`/${tenantSlug}/dashboard`, { replace: true });
    }
    // If URL has no tenant prefix and is a known page, redirect with slug
    const knownPages = ['overview', 'dashboard', 'strategic-alignment', 'beacon', 'portfolios', 'requests', 'projects', 'financials', 'capacity', 'okrs', 'roadmaps', 'raid', 'templates', 'timesheets', 'settings', 'user-settings', 'team', 'management', 'org-admin', 'workspace'];
    const firstSegment = path.split('/')[1];
    if (firstSegment && knownPages.includes(firstSegment) && firstSegment !== tenantSlug) {
      navigate(`/${tenantSlug}${path}`, { replace: true });
    }
  }, [location.pathname, navigate, isAuthenticated, isLoading, tenantSlug]);

  const logout = async () => {
    localStorage.removeItem('token');
    setToken(null);
    setDbUser(null);
    setApiTokens({ accessToken: null });
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const copyInviteLink = () => {
    if (currentTenant?.code) {
      const url = `${window.location.origin}?invite=${currentTenant.code}`;
      navigator.clipboard.writeText(url);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    }
  };


  const getActiveView = (): ViewType => {
    // Strip tenant slug prefix to get the page segment
    const path = base ? location.pathname.replace(base, '') : location.pathname;
    if (path.startsWith('/overview')) return 'overview';
    if (path.startsWith('/strategic-alignment')) return 'strategic-alignment';
    if (path.startsWith('/beacon')) return 'beacon';
    if (path.startsWith('/portfolios')) return 'portfolios';
    if (path.startsWith('/requests')) return 'requests';
    if (path.startsWith('/projects')) return 'projects';
    if (path.startsWith('/financials')) return 'financials';
    if (path.startsWith('/capacity')) return 'capacity';
    if (path.startsWith('/okrs')) return 'okrs';
    if (path.startsWith('/roadmaps')) return 'roadmaps';
    if (path.startsWith('/raid')) return 'raid';
    if (path.startsWith('/templates')) return 'templates';
    if (path.startsWith('/timesheets')) return 'timesheets';
    if (path.startsWith('/settings') || path.startsWith('/user-settings')) return 'settings';
    if (path.startsWith('/team')) return 'team';
    if (path.startsWith('/management') || path.startsWith('/org-admin')) return 'management';
    return 'dashboard';
  };

  const activeView = getActiveView();

  const handleViewChange = (view: ViewType) => {
    const viewPaths: Record<ViewType, string> = {
      overview: '/overview',
      dashboard: '/dashboard',
      'strategic-alignment': '/strategic-alignment',
      beacon: '/beacon',
      portfolios: '/portfolios',
      requests: '/requests',
      projects: '/projects',
      financials: '/financials',
      capacity: '/capacity',
      okrs: '/okrs',
      roadmaps: '/roadmaps',
      raid: '/raid',
      templates: '/templates',
      timesheets: '/timesheets',
      settings: '/settings',
      team: '/team',
      management: '/management',
    };
    navigate(`${base}${viewPaths[view]}`);
  };

  const handleSelectProject = (id: string) => {
    setSelectedProjectId(id);
    navigate(`${base}/projects`);
  };

  const canManage = !!dbUser?.isOrgAdmin || !!dbUser?.hasReports || hasPageAccess(dbUser, 'settings');
  const navigation = useMemo(() => {
    const nav = ALL_NAV_ITEMS.filter(item => hasPageAccess(dbUser, item.id));
    if (canManage) {
      nav.push({ id: 'management', label: 'Management', path: '/management' });
    }
    return nav;
  }, [dbUser, canManage]);

  const initiallyLoading = isLoading || (tenantLoading && !currentTenant) || (isAuthenticated && dbUserLoading && !dbUser);
  if (initiallyLoading) {
    return (
      <div className="app-loader">
        <img src="/NexusLogo.png" alt="Nexus" className="app-loader-logo" />
        <div className="app-loader-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const guardPage = (pageId: string, component: React.ReactNode) =>
    hasPageAccess(dbUser, pageId) ? component : <Navigate to={`${base}/dashboard`} replace />;

  return (
    <div className="App">
      <div className="app-layout">

        {/* Backdrop for mobile overlay */}
        {!sidebarCollapsed && window.innerWidth <= 1024 && (
          <div className="sidebar-backdrop" onClick={() => setSidebarCollapsed(true)} />
        )}

        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <img src="/NexusLogo.png" alt="Nexus" className="sidebar-logo-img" />
            </div>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(true)}
              aria-label="Close sidebar"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <nav className="sidebar-nav">
            {navigation.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                onClick={() => handleViewChange(item.id)}
                title={item.label}
              >
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="sidebar-footer">
            {!sidebarCollapsed && (
              <>
                <div className="sidebar-info">
                  <p className="info-label">Workforce Management</p>
                  <p className="info-version">v1.1.0</p>
                </div>
                <div className="sidebar-integrations">
                  <span className="integration-status-dot" title="Jellyfish"></span>
                  <span className="integration-status-dot" title="Jira"></span>
                  <span className="integration-status-dot" title="Confluence"></span>
                </div>
              </>
            )}
          </div>
        </aside>

        <div className="main-content">
          <header className="main-header">
            <div className="header-left">
              {sidebarCollapsed && (
                <button className="sidebar-open-btn" onClick={() => setSidebarCollapsed(false)} aria-label="Open sidebar">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                </button>
              )}
              <div className="header-breadcrumb">
                <span className="breadcrumb-item">{navigation.find(n => n.id === activeView)?.label || (activeView === 'settings' ? 'Settings' : 'Dashboard')}</span>
              </div>
            </div>
            <div className="header-actions">
              <div className="profile-dropdown" ref={profileRef}>
                <button
                  className="profile-trigger"
                  onClick={() => setProfileOpen(!profileOpen)}
                >
                  <div className="profile-avatar">
                    {(dbUser?.firstName?.[0] || user?.username?.[0] || 'U').toUpperCase()}
                    {(dbUser?.lastName?.[0] || '').toUpperCase()}
                  </div>
                  <span className="profile-name">{dbUser?.displayName || user?.username || user?.email || 'User'}</span>
                  <svg className={`profile-chevron ${profileOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </button>

                {profileOpen && (
                  <div className="profile-menu">
                    <div className="profile-menu-header">
                      <p className="profile-menu-name">{dbUser?.displayName || user?.username || 'User'}</p>
                      <p className="profile-menu-email">{user?.email || dbUser?.email}</p>
                      {currentTenant && <p className="profile-menu-tenant">{currentTenant.name}</p>}
                    </div>

                    <div className="profile-menu-section">
                      <button className="profile-menu-item" onClick={() => { setProfileOpen(false); navigate(`${base}/user-settings`); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        My Profile
                      </button>
                    </div>

                    <div className="profile-menu-section">
                      {currentTenant?.code && (
                        <button className="profile-menu-item" onClick={copyInviteLink}>
                          {inviteCopied ? (
                            <>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              <span style={{ color: '#059669' }}>Copied!</span>
                            </>
                          ) : (
                            <>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                              Copy Invite Link
                            </>
                          )}
                        </button>
                      )}
                      <button className="profile-menu-item" onClick={() => { setProfileOpen(false); setAddOrgOpen(true); setAddOrgMode(null); setAddOrgError(''); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Add Organization
                      </button>
                    </div>

                    {myTenants.length > 1 && (
                      <div className="profile-menu-section">
                        <p className="profile-menu-section-label">Switch Organization</p>
                        {myTenants.map(t => (
                          <button
                            key={t.id}
                            className={`profile-menu-item ${currentTenant?.slug === t.slug ? 'active' : ''}`}
                            disabled={currentTenant?.slug === t.slug || switchingTenant === t.id}
                            onClick={() => handleSwitchTenant(t.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            {t.name}
                            {currentTenant?.slug === t.slug && <span className="org-current-badge">Current</span>}
                            {switchingTenant === t.id && <span className="org-switching">Switching...</span>}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="profile-menu-section profile-menu-danger">
                      <button className="profile-menu-item logout" onClick={() => { setProfileOpen(false); logout(); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="content-area">
            <Routes>
              <Route path="/" element={<Navigate to={`${base}/dashboard`} replace />} />
              <Route path="/:tenantSlug/overview" element={guardPage('overview', <ExecutiveDashboard />)} />
              <Route path="/:tenantSlug/dashboard" element={<Dashboard />} />
              <Route path="/:tenantSlug/workspace" element={<Navigate to={`${base}/dashboard`} replace />} />
              <Route path="/:tenantSlug/strategic-alignment" element={guardPage('strategic-alignment', <StrategicAlignmentPage />)} />
              <Route path="/:tenantSlug/beacon" element={guardPage('beacon', <Beacon />)} />
              <Route path="/:tenantSlug/portfolios" element={guardPage('portfolios', <Portfolios />)} />
              <Route path="/:tenantSlug/portfolios/:id" element={guardPage('portfolios', <PortfolioDetail />)} />
              <Route path="/:tenantSlug/requests" element={guardPage('requests', <Requests />)} />
              <Route path="/:tenantSlug/projects" element={guardPage('projects', <ProjectList onSelectProject={handleSelectProject} selectedProjectId={selectedProjectId || undefined} />)} />
              <Route path="/:tenantSlug/projects/:projectKey" element={guardPage('projects', <ProjectList onSelectProject={handleSelectProject} selectedProjectId={selectedProjectId || undefined} />)} />
              <Route path="/:tenantSlug/projects/internal/:id" element={guardPage('projects', <InternalProjectDetail />)} />
              <Route path="/:tenantSlug/financials" element={guardPage('financials', <FinancialsPage />)} />
              <Route path="/:tenantSlug/capacity" element={guardPage('capacity', <CapacityPlanningPage />)} />
              <Route path="/:tenantSlug/okrs" element={guardPage('okrs', <OkrsPage />)} />
              <Route path="/:tenantSlug/roadmaps" element={guardPage('roadmaps', <RoadmapsPage />)} />
              <Route path="/:tenantSlug/raid" element={guardPage('raid', <Raid />)} />
              <Route path="/:tenantSlug/templates" element={guardPage('templates', <TemplatesPage />)} />
              <Route path="/:tenantSlug/timesheets" element={guardPage('timesheets', <TimesheetsPage />)} />
              <Route path="/:tenantSlug/settings" element={<Settings />} />
              <Route path="/:tenantSlug/user-settings" element={<UserSettings />} />
              <Route path="/:tenantSlug/team" element={guardPage('team', <TeamManagement />)} />
              <Route path="/:tenantSlug/org-admin" element={<Navigate to={`${base}/management`} replace />} />
              <Route path="/:tenantSlug/management" element={canManage ? <OrgAdminPage /> : <Navigate to={`${base}/dashboard`} replace />} />
              {/* Catch old routes without slug prefix */}
              <Route path="*" element={<Navigate to={`${base}/dashboard`} replace />} />
            </Routes>
            <div style={{ marginTop: location.pathname.includes('/projects') ? 16 : 0 }}>
              <InlineDiscussion />
            </div>
          </main>
          <footer className="main-footer">
            <div className="footer-content">
              <span>Nexus - Workforce Management</span>
              <span className="footer-links">
                <button type="button" className="footer-link" onClick={() => handleViewChange('settings')}>Settings</button>
                <span className="separator">•</span>
                <a href="https://github.com/LifeStanceHealth" target="_blank" rel="noopener noreferrer">GitHub</a>
                <span className="separator">•</span>
                <button type="button" className="footer-link">Support</button>
              </span>
            </div>
          </footer>
        </div>
      </div>

      {/* Add Organization Modal */}
      {addOrgOpen && (
        <div className="add-org-overlay" onClick={() => setAddOrgOpen(false)}>
          <div className="add-org-modal" onClick={(e) => e.stopPropagation()}>
            <div className="add-org-header">
              <h3>Add Organization</h3>
              <button className="add-org-close" onClick={() => setAddOrgOpen(false)}>&times;</button>
            </div>

            {addOrgError && <p className="add-org-error">{addOrgError}</p>}

            {!addOrgMode && (
              <div className="add-org-choices">
                <button className="add-org-choice" onClick={() => setAddOrgMode('create')}>
                  <div className="add-org-choice-icon create">&#x1F3E2;</div>
                  <span className="add-org-choice-label">Create a New Organization</span>
                </button>
                <button className="add-org-choice" onClick={() => setAddOrgMode('join')}>
                  <div className="add-org-choice-icon join">&#x1F465;</div>
                  <span className="add-org-choice-label">Join with Code</span>
                </button>
              </div>
            )}

            {addOrgMode === 'create' && (
              <div className="add-org-form">
                <div className="add-org-form-header">
                  <span className="add-org-form-tag create">Creating a new organization</span>
                  <button onClick={() => setAddOrgMode(null)}>Back</button>
                </div>
                <input
                  type="text"
                  placeholder="Organization name"
                  value={addOrgName}
                  onChange={(e) => setAddOrgName(e.target.value)}
                  className="add-org-input"
                  autoFocus
                />
                <button
                  className="add-org-submit"
                  onClick={handleAddOrgSubmit}
                  disabled={addOrgLoading || !addOrgName.trim()}
                >
                  {addOrgLoading ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            )}

            {addOrgMode === 'join' && (
              <div className="add-org-form">
                <div className="add-org-form-header">
                  <span className="add-org-form-tag join">Join with a code</span>
                  <button onClick={() => { setAddOrgMode(null); setAddOrgCode(''); setAddOrgLookup(null); }}>Back</button>
                </div>
                <input
                  type="text"
                  placeholder="e.g. ACME-2026-XY1Z"
                  value={addOrgCode}
                  onChange={(e) => setAddOrgCode(e.target.value)}
                  className="add-org-input"
                  autoFocus
                />
                {addOrgLookup && (
                  <div className="add-org-lookup">Joining: <strong>{addOrgLookup}</strong></div>
                )}
                <button
                  className="add-org-submit green"
                  onClick={handleAddOrgSubmit}
                  disabled={addOrgLoading || !addOrgCode.trim()}
                >
                  {addOrgLoading ? 'Joining...' : 'Join Organization'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <DiscussionProvider>
          <AppContent />
        </DiscussionProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
